import { z } from 'zod'
import './registry'
import { MoneySchema } from './common'

/**
 * Response shape for `GET /records/stats` — the catalogue KPI strip. These are
 * collection-wide aggregates a paginated list can't produce, materialised on
 * the per-tenant `STATS#CATALOGUE` item by the stats-stream consumer and read
 * as one point-get. The handler reshapes the item's flat counter attributes
 * (see lib/stats-diff.ts) into this structured form.
 *
 * Photo coverage is exposed as numerator + denominator (`onlineForSaleWithPhoto`
 * over `onlineForSale`) rather than a pre-divided percentage, so the frontend
 * owns the rounding and the zero-denominator case ("— %", never NaN).
 */
export const CatalogueStatsSchema = z
  .object({
    /** All records ever taken in. */
    total: z.number().int().min(0),
    /** status === 'in_stock'. */
    inStock: z.number().int().min(0),
    /** Sum of price over in-stock records, as a money decimal-string. */
    inStockValue: MoneySchema,
    /** channel === 'online' && status === 'in_stock'. */
    onlineForSale: z.number().int().min(0),
    /** …of which at least one online image is uploaded (photo-% numerator). */
    onlineForSaleWithPhoto: z.number().int().min(0),
    /**
     * channel === 'store' && status === 'in_stock' — physical-shop stock.
     * `.default(0)` (here and on the sold-split fields below) lets a client
     * parse a response from a backend deployed BEFORE these counters existed
     * without throwing — the new KPI tiles read 0 until the API catches up.
     */
    storeForSale: z.number().int().min(0).default(0),
    /** Entered sold/sold_online/fulfilled in the current calendar month (UTC). */
    soldThisMonth: z.number().int().min(0),
    /** Online sales (sold_online/fulfilled) entered this / last calendar month. */
    soldOnlineThisMonth: z.number().int().min(0).default(0),
    soldOnlineLastMonth: z.number().int().min(0).default(0),
    /** Store sales (status 'sold') entered this / last calendar month. */
    soldStoreThisMonth: z.number().int().min(0).default(0),
    soldStoreLastMonth: z.number().int().min(0).default(0),
    /** status === 'not_for_sale'. */
    notForSale: z.number().int().min(0),
  })
  .openapi('CatalogueStats')

export type CatalogueStats = z.infer<typeof CatalogueStatsSchema>
